import {useQuery, keepPreviousData} from "@tanstack/react-query";
import {ApiConfig} from "@/config/api.config";
import {ApiHookProps} from '@/@types/api.types';
import {UrlHelper} from '@/utils/UrlHelper';
import {TransactionListResponseDto} from '@/features/transactions/types/transactions-dto';
import {useTransactionsFilter} from '@/features/transactions/hooks/useTransactionsFilter';
import {NumbersHelper} from '@/utils/numbersHelper';
import {DateTimeHelper} from '@/utils/dateTimeHelper';
import {useAxiosAuth} from "@/hooks/useAxiosAuth.ts";
import {useCurrencyStore} from "@/store/useCurrencyStore.ts";

type Props = {
    listSize?: number;
    ownerId?: string;
} & ApiHookProps;

export const useTransactionsListApi = ({searchText, listSize, ownerId}: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const {filter} = useTransactionsFilter();
    const {take, skip, startDate, endDate, amountRange, apartmentId, account, orderBy, orderDirection} = filter;
    const {currentCurrency} = useCurrencyStore();

    const apartmentIds = apartmentId?.map((apartment) => apartment.id);
    const {min, max} = NumbersHelper.getMinMaxValues(amountRange);

    const {
        data: transactionsListData,
        isLoading: isLoadingTransactionsListData,
        isError: isErrorTransactionsListData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh,
            ApiConfig.queries.features.transactions.queryKeys.list,
            take, skip, startDate, endDate, amountRange, apartmentId, account, orderBy, orderDirection, listSize, searchText, currentCurrency, ownerId

        ],
        queryFn: async (): Promise<TransactionListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.transactions.endpoints.list, {
                startDate: DateTimeHelper.getDateOnly(startDate),
                endDate: DateTimeHelper.getDateOnly(endDate),
                apartmentId: apartmentIds,
                minAmount: min,
                maxAmount: max,
                take: listSize ?? take,
                skip,
                account,
                orderBy,
                orderDirection,
                searchText,
                ownerId
            });
            const response = await axiosInstance.get<TransactionListResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData
    });

    return {
        transactionsListData,
        isLoadingTransactionsListData,
        isErrorTransactionsListData,
    }
};
